import { useMemo } from 'react';
import { I18nManager, Platform, type TextStyle, type ViewStyle } from 'react-native';
import { isWebRuntime } from '../platform/runtimeTarget';

/** Mirrors React Native’s layout direction (after `forceRTL` / restart policy). */
export function isRTL(): boolean {
  try {
    if (isWebRuntime() && typeof document !== 'undefined') {
      const dir = document.documentElement?.dir || document.body?.dir;
      if (dir === 'rtl') {
        return true;
      }
      if (dir === 'ltr') {
        return false;
      }
    }
    return I18nManager?.isRTL === true;
  } catch {
    return false;
  }
}

/** Text alignment for translatable copy (start edge in LTR/RTL). */
export function rtlTextAlign(): 'left' | 'right' {
  return isRTL() ? 'right' : 'left';
}

/** `writingDirection` for translatable copy. */
export function rtlWritingDirection(): 'rtl' | 'ltr' {
  return isRTL() ? 'rtl' : 'ltr';
}

// --- Layout isolation (axis B) ---

/**
 * RN-web StyleSheet.validate rejects `direction` ("Did you mean writingDirection?").
 * Native Yoga still uses `direction` for layout islands.
 */
export function layoutDirectionStyle(dir: 'ltr' | 'rtl'): ViewStyle {
  if (Platform.OS === 'web') {
    return { writingDirection: dir };
  }
  return { direction: dir };
}

/** Force this subtree to LTR Yoga (date sliders, chronological carousels). */
export const layoutLtrStyle: ViewStyle = layoutDirectionStyle('ltr');

/** Force this subtree to RTL Yoga (nested RTL inside an LTR island, or force RTL in LTR app). */
export const layoutRtlStyle: ViewStyle = layoutDirectionStyle('rtl');

/** @deprecated Prefer `layoutLtrStyle` — alias kept for existing StyleSheets. */
export const horizontalLtrDirectionStyle: ViewStyle = layoutLtrStyle;

/** Chronological horizontal rails / date strips — absolute LTR island. */
export const scopeChronologicalHorizontal: ViewStyle = layoutLtrStyle;

/** Nested forced-RTL subtree (absolute). */
export const scopeForceRtlSubtree: ViewStyle = layoutRtlStyle;

export type LayoutStyleOptions = {
  /** `app` follows `I18nManager.isRTL`. Prefer absolute `ltr`/`rtl` for islands. */
  direction?: 'ltr' | 'rtl' | 'app';
  /** `row-app` = row in LTR app, row-reverse in RTL app. */
  row?: 'row' | 'row-reverse' | 'row-app';
};

export function layoutStyle(options: LayoutStyleOptions = {}): ViewStyle {
  const out: ViewStyle = {};
  if (options.direction === 'ltr' || options.direction === 'rtl') {
    Object.assign(out, layoutDirectionStyle(options.direction));
  } else if (options.direction === 'app') {
    Object.assign(out, layoutDirectionStyle(isRTL() ? 'rtl' : 'ltr'));
  }
  if (options.row === 'row') {
    out.flexDirection = 'row';
  } else if (options.row === 'row-reverse') {
    out.flexDirection = 'row-reverse';
  } else if (options.row === 'row-app') {
    out.flexDirection = isRTL() ? 'row-reverse' : 'row';
  }
  return out;
}

/**
 * Horizontal `ScrollView` / lists in RTL often need LTR direction so indices and gestures match
 * design (chronological sliders, filters). Merge with your scroll `style` prop.
 */
/** Force chronological horizontal strips to LTR (always — safe in LTR apps too). */
export function horizontalScrollStyle(): ViewStyle {
  return layoutLtrStyle;
}

/**
 * Row containers that should mirror in RTL (ambient app RTL only).
 * Do not stack on a parent that is already an LTR island — that double-flips.
 */
export function flipRowStyle(): ViewStyle | undefined {
  return isRTL() ? { flexDirection: 'row-reverse' } : undefined;
}

/** Same as `flipRowStyle` — named for the isolation API. */
export function scopeMirrorRowWhenAppRtl(): ViewStyle | undefined {
  return flipRowStyle();
}

// --- Text isolation (axis C) ---

export type TextStyleOptions = {
  /**
   * `start`/`end` resolve from **app** RTL (locale reading edge), not from a parent LTR island.
   * Use absolute `left`/`right` when hugging a physical edge inside an LTR media row.
   */
  align?: 'start' | 'end' | 'left' | 'right' | 'center';
  /** `locale` follows app RTL for glyph order. */
  writing?: 'locale' | 'ltr' | 'rtl';
  /** Full-width stretch — keeps multiline Arabic paragraphs from shrink-wrapping. */
  stretch?: boolean;
};

function resolve_text_align(align: TextStyleOptions['align']): TextStyle['textAlign'] {
  if (align == null || align === 'start') {
    return rtlTextAlign();
  }
  if (align === 'end') {
    return isRTL() ? 'left' : 'right';
  }
  return align;
}

function resolve_writing(writing: TextStyleOptions['writing']): 'rtl' | 'ltr' {
  if (writing == null || writing === 'locale') {
    return rtlWritingDirection();
  }
  return writing;
}

export function textStyle(options: TextStyleOptions = {}): TextStyle {
  const align = options.align ?? 'start';
  const writing = options.writing ?? 'locale';
  const stretch = options.stretch !== false;
  const style: TextStyle = {
    textAlign: resolve_text_align(align),
    writingDirection: resolve_writing(writing),
  };
  if (stretch) {
    style.width = '100%';
    style.alignSelf = 'stretch';
  }
  return style;
}

/** Full-width localized copy (titles, descriptions). */
export function textFollowLocale(): TextStyle {
  return textStyle({ align: 'start', writing: 'locale', stretch: true });
}

/** Glyphs follow locale; `textAlign: 'left'` for leading-media rows inside LTR islands. */
export function textLocaleGlyphsAlignLeft(): TextStyle {
  return textStyle({ align: 'left', writing: 'locale', stretch: true });
}

/** Start-align + locale writing + stretch (same as `textFollowLocale`). */
export function textLocaleGlyphsAlignStart(): TextStyle {
  return textStyle({ align: 'start', writing: 'locale', stretch: true });
}

/** Latin-only values (email, codes, SKUs) inside Arabic screens. */
export function textForceLtr(): TextStyle {
  return textStyle({ align: 'left', writing: 'ltr', stretch: false });
}

/** Force RTL text metrics inside an LTR island. */
export function textForceRtl(): TextStyle {
  return textStyle({ align: 'right', writing: 'rtl', stretch: true });
}

export function textCenterLocale(): TextStyle {
  return textStyle({ align: 'center', writing: 'locale', stretch: true });
}

/** @deprecated Prefer `textFollowLocale`. */
export function rtlLocalizedTextStyle(): TextStyle {
  return textFollowLocale();
}

/** @deprecated Prefer `textLocaleGlyphsAlignLeft` / `scopeLeadingMediaRow`. */
export function rtlLeadingMediaCopyTextStyle(): TextStyle {
  return textLocaleGlyphsAlignLeft();
}

// --- Combined scopes ---

export type LayoutTextScope = {
  container: ViewStyle;
  text: TextStyle;
};

export function scopeLtrLayoutLocaleText(): LayoutTextScope {
  return {
    container: layoutLtrStyle,
    text: textFollowLocale(),
  };
}

export function scopeLtrLayoutLtrText(): LayoutTextScope {
  return {
    container: layoutLtrStyle,
    text: textForceLtr(),
  };
}

/** LTR media row (image stays physically left) + locale glyphs with left align. */
export function scopeLeadingMediaRow(): { row: ViewStyle; copy: TextStyle } {
  return {
    row: layoutLtrStyle,
    copy: textLocaleGlyphsAlignLeft(),
  };
}

export function scopeNestedRtlLocaleText(): LayoutTextScope {
  return {
    container: layoutRtlStyle,
    text: textFollowLocale(),
  };
}

export function scopeNestedLtr(options?: { text?: 'locale' | 'ltr' }): LayoutTextScope {
  const text_mode = options?.text ?? 'locale';
  return {
    container: layoutLtrStyle,
    text: text_mode === 'ltr' ? textForceLtr() : textFollowLocale(),
  };
}

// --- Icons / chevrons (app RTL) ---

export function leadingBackArrowIcon(): 'arrow-right' | 'arrow-left' {
  return isRTL() ? 'arrow-right' : 'arrow-left';
}

export function chevronPreviousAlongAxis(): 'arrow-left' | 'arrow-right' {
  return isRTL() ? 'arrow-right' : 'arrow-left';
}

export function chevronNextAlongAxis(): 'arrow-left' | 'arrow-right' {
  return isRTL() ? 'arrow-left' : 'arrow-right';
}

export function trailingRowDisclosureChevron(): 'arrow-right' | 'arrow-left' {
  return isRTL() ? 'arrow-left' : 'arrow-right';
}

export function sectionHeaderChevronStart(): 'arrow-right' | 'arrow-left' {
  return isRTL() ? 'arrow-right' : 'arrow-left';
}

export function sectionHeaderChevronEnd(): 'arrow-left' | 'arrow-right' {
  return isRTL() ? 'arrow-left' : 'arrow-right';
}

// --- useRtl ---

export type UseRtlResult = {
  isRTL: boolean;
  textAlign: 'left' | 'right';
  writingDirection: 'rtl' | 'ltr';
  /** @deprecated Prefer `textFollowLocale`. */
  localizedTextStyle: TextStyle;
  /** @deprecated Prefer `textLocaleGlyphsAlignLeft`. */
  leadingMediaCopyTextStyle: TextStyle;
  textFollowLocale: TextStyle;
  textLocaleGlyphsAlignLeft: TextStyle;
  textLocaleGlyphsAlignStart: TextStyle;
  textForceLtr: TextStyle;
  textForceRtl: TextStyle;
  textCenterLocale: TextStyle;
  layoutLtrStyle: ViewStyle;
  layoutRtlStyle: ViewStyle;
  horizontalScrollStyle: ViewStyle;
  horizontalLtrDirectionStyle: ViewStyle;
  scopeChronologicalHorizontal: ViewStyle;
  flipRowStyle: ViewStyle | undefined;
  leadingBackArrowIcon: 'arrow-right' | 'arrow-left';
  chevronPreviousAlongAxis: 'arrow-left' | 'arrow-right';
  chevronNextAlongAxis: 'arrow-left' | 'arrow-right';
  trailingRowDisclosureChevron: 'arrow-right' | 'arrow-left';
  sectionHeaderChevronStart: 'arrow-right' | 'arrow-left';
  sectionHeaderChevronEnd: 'arrow-left' | 'arrow-right';
  layoutStyle: typeof layoutStyle;
  textStyle: typeof textStyle;
  scopeLtrLayoutLocaleText: typeof scopeLtrLayoutLocaleText;
  scopeLtrLayoutLtrText: typeof scopeLtrLayoutLtrText;
  scopeLeadingMediaRow: typeof scopeLeadingMediaRow;
  scopeNestedRtlLocaleText: typeof scopeNestedRtlLocaleText;
  scopeNestedLtr: typeof scopeNestedLtr;
  scopeMirrorRowWhenAppRtl: typeof scopeMirrorRowWhenAppRtl;
};

/**
 * Stable RTL layout helpers for screens and lists. Prefer this over `I18nManager.isRTL` in UI code.
 */
export function useRtl(): UseRtlResult {
  const rtl = isRTL();
  return useMemo(
    () => ({
      isRTL: rtl,
      textAlign: rtlTextAlign(),
      writingDirection: rtlWritingDirection(),
      localizedTextStyle: textFollowLocale(),
      leadingMediaCopyTextStyle: textLocaleGlyphsAlignLeft(),
      textFollowLocale: textFollowLocale(),
      textLocaleGlyphsAlignLeft: textLocaleGlyphsAlignLeft(),
      textLocaleGlyphsAlignStart: textLocaleGlyphsAlignStart(),
      textForceLtr: textForceLtr(),
      textForceRtl: textForceRtl(),
      textCenterLocale: textCenterLocale(),
      layoutLtrStyle,
      layoutRtlStyle,
      horizontalScrollStyle: horizontalScrollStyle(),
      horizontalLtrDirectionStyle: layoutLtrStyle,
      scopeChronologicalHorizontal,
      flipRowStyle: flipRowStyle(),
      leadingBackArrowIcon: leadingBackArrowIcon(),
      chevronPreviousAlongAxis: chevronPreviousAlongAxis(),
      chevronNextAlongAxis: chevronNextAlongAxis(),
      trailingRowDisclosureChevron: trailingRowDisclosureChevron(),
      sectionHeaderChevronStart: sectionHeaderChevronStart(),
      sectionHeaderChevronEnd: sectionHeaderChevronEnd(),
      layoutStyle,
      textStyle,
      scopeLtrLayoutLocaleText,
      scopeLtrLayoutLtrText,
      scopeLeadingMediaRow,
      scopeNestedRtlLocaleText,
      scopeNestedLtr,
      scopeMirrorRowWhenAppRtl,
    }),
    [rtl],
  );
}
